import { useCallback, useState, useEffect, useRef } from 'react';
import ReactFlow, {
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    addEdge,
    type Node,
    type Edge,
    type Connection,
    BackgroundVariant,
    type ReactFlowInstance,
    Panel,
    ConnectionMode
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Save, Loader2, ArrowLeft, LayoutGrid, Zap, RefreshCw, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import CustomEdge from './CustomEdge';
import AssetsPalette from './AssetsPalette';
import SwitchNode from './nodes/SwitchNode';
import AssetNode from './nodes/AssetNode';
import AddConnectionModal from './AddConnectionModal';
import TopologyLinkModal from './TopologyLinkModal';
import UnlinkModal from './UnlinkModal';
import DeleteLinkModal from './DeleteLinkModal';
import AssetDetailSidebar from './AssetSidebar/AssetDetailSidebar';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const nodeTypes = {
    switchNode: SwitchNode,
    assetNode: AssetNode,
};

const edgeTypes = {
    custom: CustomEdge,
};

let nodeIdCounter = 0;
const getNodeId = () => `node-${nodeIdCounter++}`;

// ─── Tree Layout Helper ────────────────────────────────────────────────────────

const X_SPACING = 280;
const Y_SPACING = 180;

function computeTreePositions(
    topologyNodes: any[],
    topologyEdges: any[]
): Map<number, { x: number; y: number }> {
    const positionMap = new Map<number, { x: number; y: number }>();

    // Build parent → children and child → parent maps from edges
    const childrenMap = new Map<number, number[]>();
    const parentMap = new Map<number, number>();

    topologyEdges.forEach((edge: any) => {
        const parentId = edge.upstream_asset_id;
        const childId = edge.downstream_asset_id;
        if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
        childrenMap.get(parentId)!.push(childId);
        parentMap.set(childId, parentId);
    });

    // Find root nodes — those with no parent in the edge list
    const allNodeIds = topologyNodes.map((n: any) => n.id);
    const roots = allNodeIds.filter((id: number) => !parentMap.has(id));

    // BFS to assign depth and sibling index
    const depthMap = new Map<number, number>();
    const xIndexMap = new Map<number, number>();
    const depthCounters = new Map<number, number>();

    const queue: { id: number; depth: number }[] = roots.map((id: number) => ({
        id,
        depth: 0,
    }));

    while (queue.length > 0) {
        const { id, depth } = queue.shift()!;
        if (depthMap.has(id)) continue;

        depthMap.set(id, depth);
        const xIndex = depthCounters.get(depth) ?? 0;
        xIndexMap.set(id, xIndex);
        depthCounters.set(depth, xIndex + 1);

        const children = childrenMap.get(id) || [];
        children.forEach((childId: number) => {
            queue.push({ id: childId, depth: depth + 1 });
        });
    }

    // Handle disconnected nodes not reached by BFS
    topologyNodes.forEach((item: any) => {
        if (!depthMap.has(item.id)) {
            const depth = 0;
            const xIndex = depthCounters.get(depth) ?? 0;
            depthMap.set(item.id, depth);
            xIndexMap.set(item.id, xIndex);
            depthCounters.set(depth, xIndex + 1);
        }
    });

    // Compute final pixel positions — center siblings horizontally per depth
    topologyNodes.forEach((item: any) => {
        const depth = depthMap.get(item.id) ?? 0;
        const xIndex = xIndexMap.get(item.id) ?? 0;
        const siblingsAtDepth = depthCounters.get(depth) ?? 1;
        const totalWidth = (siblingsAtDepth - 1) * X_SPACING;

        positionMap.set(item.id, {
            x: xIndex * X_SPACING - totalWidth / 2,
            y: depth * Y_SPACING,
        });
    });

    return positionMap;
}

// ──────────────────────────────────────────────────────────────────────────────

export default function TopologyBuilder() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

    const [assets, setAssets] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [, setHasUnsavedChanges] = useState(false);

    const [viewMode, setViewMode] = useState<'overview' | 'detail'>('overview');
    const [focusedSwitchName, setFocusedSwitchName] = useState<string>('');
    const [focusedSwitchId, setFocusedSwitchId] = useState<number | null>(null);

    const level = useClientLevel();
    const [usedAssetIds, setUsedAssetIds] = useState<Set<number>>(new Set());
    const [overviewAssetIds, setOverviewAssetIds] = useState<Set<number>>(new Set());

    const [connectionModalOpen, setConnectionModalOpen] = useState(false);
    const [topologyLinkModalOpen, setTopologyLinkModalOpen] = useState(false);
    const [pendingConnection, setPendingConnection] = useState<Connection | null>(null);
    const [connectionInfo, setConnectionInfo] = useState<{
        sourceId: number;
        targetId: number;
        sourceName: string;
        targetName: string;
    } | null>(null);

    const [unlinkModalState, setUnlinkModalState] = useState<{
        open: boolean;
        interfaceId: number | null;
        nodeId: string | null;
        assetName: string;
    }>({ open: false, interfaceId: null, nodeId: null, assetName: '' });
    const [isUnlinking, setIsUnlinking] = useState(false);

    const [deleteLinkModalState, setDeleteLinkModalState] = useState<{
        open: boolean;
        edgeId: string | null;
        linkId: number | null;
    }>({ open: false, edgeId: null, linkId: null });
    const [isDeletingLink, setIsDeletingLink] = useState(false);
    const navigate = useNavigate();

    // ─── Asset Detail Sidebar ───────────────────────────────────────────────────
    const [sidebarState, setSidebarState] = useState<{
        open: boolean;
        assetId: number;
        assetName: string;
        assetType: string;
        assetIp: string;
    }>({ open: false, assetId: 0, assetName: '', assetType: '', assetIp: '' });

    const [isPaletteOpen, setIsPaletteOpen] = useState(true);
    const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
    const [switchGroups, setSwitchGroups] = useState<any[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as any)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ─── Fetching ──────────────────────────────────────────────────────────────

    const fetchAssets = useCallback(async () => {
        try {
            setLoading(true);
            const response = await baseapi.get('/assets', {
                params: {
                    query: JSON.stringify(level?.id ? { client_id: level.id } : {}),
                    skip: 0,
                    limit: 1000,
                    connection_check: true,
                },
            });
            if (response.data.status) setAssets(response.data.data);
            else setAssets([]);
        } catch (error: any) {
            showError(error.message || 'Failed to fetch assets');
        } finally {
            setLoading(false);
        }
    }, [level?.id]);

    const handlePortClick = useCallback((port: any) => {
        setIsPaletteOpen(false);
        setSidebarState({
            open: true,
            assetId: port.id,
            assetName: port.port_name || port.port_number || `Port ${port.id}`,
            assetType: 'interface',
            assetIp: '-',
        });
    }, []);

    const processOverviewData = (data: any) => {
        const topologyNodes = data.nodes || [];
        const topologyEdges = data.edges || [];
        const initialNodes: Node[] = [];
        const processedIds = new Set<number>();

        // Track global overview IDs for the assets palette
        const overviewIds = new Set<number>();
        topologyNodes.forEach((n: any) => { if (n.id) overviewIds.add(n.id); });
        setOverviewAssetIds(overviewIds);

        // Update groups list
        const groups = data.switch_groups || data.groups || [];
        setSwitchGroups(groups);

        const hasAnyNullPosition = topologyNodes.some(
            (n: any) => n.position === null || n.position === undefined
        );

        const treePositions = computeTreePositions(topologyNodes, topologyEdges);

        topologyNodes.forEach((item: any) => {
            if (processedIds.has(item.id)) return;
            processedIds.add(item.id);

            let parsedInterfaces: any[] = [];
            try {
                if (item.interfaces) {
                    parsedInterfaces =
                        typeof item.interfaces === 'string'
                            ? JSON.parse(item.interfaces)
                            : item.interfaces;
                }
            } catch (err) {
                console.error(`Failed to parse interfaces for node ${item.name}`, err);
            }

            const isSwitchLike =
                item.type === 'switch' || item.type === 'firewall' || item.type === 'hub';

            const position = hasAnyNullPosition
                ? treePositions.get(item.id) ?? { x: 0, y: 0 }
                : { x: item.position.x, y: item.position.y };

            initialNodes.push({
                id: `asset-${item.id}`,
                type: 'assetNode',
                position,
                data: {
                    assetId: item.id,
                    switchName: item.name,
                    assetName: item.name,
                    assetType: item.type,
                    assetIp: item.ip_address || '',
                    status: item.status,
                    hasProblem: item.has_problem,
                    isTop: false,
                    preloadedInterfaces: parsedInterfaces,
                    onDelete: handleAssetNodeDelete,
                    portMappings: isSwitchLike ? parsedInterfaces : undefined,
                    onPortClick: (port: any) => handlePortClick(port),
                },
                draggable: true,
            });
        });

        const initialEdges: Edge[] = topologyEdges.map((edge: any) => {
            const defaultStyle = { stroke: '#10b981', strokeWidth: 4, opacity: 0.8 };
            const sourcePort = edge.source_port ? `${edge.source_port}` : '';
            const targetPort = edge.target_port ? `${edge.target_port}` : '';
            const connType = edge.connection_type ? `(${edge.connection_type})` : '';
            const label = [sourcePort, targetPort].filter(Boolean).join(' → ') + (connType ? ` ${connType}` : '');

            return {
                id: `overview-edge-${edge.id || `${edge.upstream_asset_id}-${edge.downstream_asset_id}`}`,
                source: `asset-${edge.upstream_asset_id}`,
                target: `asset-${edge.downstream_asset_id}`,
                sourceHandle: 'bottom',
                targetHandle: 'top',
                type: 'custom',
                animated: true,
                label: label,
                labelStyle: {
                    fill: '#ffffff',
                    fontSize: 10,
                    fontWeight: 500,
                    pointerEvents: 'none'
                },
                labelBgStyle: {
                    fill: '#1f2937',
                    fillOpacity: 0.9,
                    rx: 4,
                    ry: 4
                },
                labelBgPadding: [4, 2],
                style: defaultStyle,
                data: {
                    connection_type: edge.connection_type,
                    is_uplink: edge.is_uplink,
                    linkId: edge.id,
                    originalStyle: defaultStyle,
                    sourcePort: edge.source_port,
                    targetPort: edge.target_port,
                },
            };
        });

        setNodes(initialNodes);
        setEdges(initialEdges);
        setHasUnsavedChanges(false);
    };

    const fetchTopologyOverview = useCallback(async (groupId?: string) => {
        try {
            setLoading(true);
            const client_id = level.name === "global" ? null : level.id;
            const params: any = { client_id };
            const activeGroupId = groupId !== undefined ? groupId : selectedGroupId;
            if (activeGroupId && activeGroupId !== 'all') {
                params.switch_group_id = activeGroupId;
            }
            const response = await baseapi.get('/topology/overview', { params });
            if (response.data.status && response.data.data) {
                processOverviewData(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch topology data', error);
            showError('Failed to load topology');
        } finally {
            setLoading(false);
        }
    }, [level, selectedGroupId]);

    const processDetailData = (
        switchId: number,
        switchName: string,
        centerPos: { x: number; y: number },
        interfaces: any[],
        switchType: string,
        switchIp: string,
        hasProblem: boolean
    ) => {
        const connections: any[] = [];
        const seenAssetIds = new Set<number>();

        interfaces.forEach((inf: any) => {
            let assetsInPort: any[] = [];
            if (inf.connected_assets) {
                try {
                    const parsed = typeof inf.connected_assets === 'string'
                        ? JSON.parse(inf.connected_assets)
                        : inf.connected_assets;
                    if (Array.isArray(parsed)) assetsInPort = parsed;
                } catch { /* ignore */ }
            }

            if (assetsInPort.length === 0 && (inf.connected_asset || inf.connected_asset_id)) {
                const asset = inf.connected_asset || assets.find(a => a.id === inf.connected_asset_id);
                if (asset) {
                    assetsInPort = [{
                        asset_id: asset.id,
                        asset_name: asset.name || asset.asset_name,
                        asset_type: asset.type || asset.asset_type,
                        asset_ip: asset.ip_address || asset.asset_ip,
                        asset_status: asset.status,
                        is_uplink: (asset._is_uplink ?? false) || (inf.is_promoted && (asset.type === 'switch' || asset.asset_type === 'switch')),
                        connection_type: asset._connection_type ?? 'ethernet',
                        has_problem: asset.has_problem,
                        detail_position: asset.detail_position || asset.position
                    }];
                }
            }

            assetsInPort.forEach(a => {
                const assetId = a.asset_id || a.id;
                if (!assetId) return;
                connections.push({
                    portId: inf.id,
                    assetId: assetId,
                    assetName: a.asset_name || a.name || 'Linked Asset',
                    assetType: a.asset_type || a.type || 'other',
                    assetIp: a.asset_ip || a.ip_address || 'Unknown',
                    assetStatus: a.asset_status || a.status,
                    isUplink: (a.is_uplink ?? false) || (inf.is_promoted && (a.asset_type === 'switch' || a.type === 'switch')),
                    connectionType: a.connection_type || 'ethernet',
                    hasProblem: a.has_problem ?? false,
                    detailPosition: a.detail_position || a.position
                });
            });
        });

        const uniqueAssetConnections = connections.filter(c => {
            if (seenAssetIds.has(c.assetId)) return false;
            seenAssetIds.add(c.assetId);
            return true;
        });

        const switchNodeId = `detail-switch-${switchId}`;
        const isAP = switchType === 'access_point';

        const switchNode: Node = {
            id: switchNodeId,
            type: isAP ? 'assetNode' : 'switchNode',
            position: centerPos,
            data: {
                switchName,
                assetName: switchName,
                assetId: switchId,
                assetType: switchType,
                assetIp: switchIp,
                status: 'active',
                hasProblem,
                portMappings: interfaces,
                onDelete: () => showError('Cannot delete the main node in detail view'),
                onPortClick: (port: any) => handlePortClick(port),
            },
            draggable: true,
        };

        const detailNodes: Node[] = [switchNode];
        const detailEdges: Edge[] = [];

        const NODE_WIDTH = 220;
        const MIN_RADIUS = 320;
        const circumferenceRadius = (uniqueAssetConnections.length * NODE_WIDTH) / (2 * Math.PI);
        const radius = Math.max(MIN_RADIUS, circumferenceRadius);
        const angleStep = (2 * Math.PI) / (uniqueAssetConnections.length || 1);
        const startAngle = -Math.PI / 2;

        uniqueAssetConnections.forEach((conn: any, index: number) => {
            const angle = startAngle + index * angleStep;
            const fallbackPos = {
                x: centerPos.x + radius * Math.cos(angle),
                y: centerPos.y + radius * Math.sin(angle),
            };

            const hasCustomPos = conn.detailPosition && typeof conn.detailPosition.x === 'number' && typeof conn.detailPosition.y === 'number';
            const assetPos = hasCustomPos
                ? { x: Number(conn.detailPosition.x), y: Number(conn.detailPosition.y) }
                : fallbackPos;

            const assetNodeId = `detail-asset-${conn.assetId}`;
            detailNodes.push({
                id: assetNodeId,
                type: 'assetNode',
                position: assetPos,
                data: {
                    assetId: conn.assetId,
                    assetName: conn.assetName,
                    assetType: conn.assetType,
                    assetIp: conn.assetIp,
                    status: conn.assetStatus,
                    hasProblem: conn.hasProblem,
                    onDelete: handleAssetNodeDelete,
                },
                draggable: true,
            });

            let apTargetHandle = 'top';
            let apSourceHandle = 'bottom';

            if (isAP) {
                if (angle >= -Math.PI / 4 && angle < Math.PI / 4) {
                    apSourceHandle = 'right';
                    apTargetHandle = 'right-target';
                } else if (angle >= Math.PI / 4 && angle < 3 * Math.PI / 4) {
                    apSourceHandle = 'bottom';
                    apTargetHandle = 'bottom-target';
                } else if (angle >= 3 * Math.PI / 4 && angle < 5 * Math.PI / 4) {
                    apSourceHandle = 'left-source';
                    apTargetHandle = 'left';
                } else {
                    apSourceHandle = 'top-source';
                    apTargetHandle = 'top';
                }
            }

            if (conn.isUplink) {
                const upStyle = { stroke: '#10b981', strokeWidth: 2 };
                detailEdges.push({
                    id: `detail-edge-${conn.assetId}-${switchId}-${conn.portId}`,
                    source: assetNodeId,
                    sourceHandle: 'bottom',
                    target: switchNodeId,
                    targetHandle: isAP ? apTargetHandle : `port-in-${conn.portId}`,
                    type: 'custom',
                    animated: true,
                    style: upStyle,
                    data: {
                        connection_type: conn.connectionType,
                        is_uplink: true,
                        originalStyle: upStyle,
                        hideLabel: isAP,
                    },
                });
            } else {
                const downStyle = { stroke: '#3b82f6', strokeWidth: 2 };
                detailEdges.push({
                    id: `detail-edge-${switchId}-${conn.assetId}-${conn.portId}`,
                    source: switchNodeId,
                    sourceHandle: isAP ? apSourceHandle : `port-${conn.portId}`,
                    target: assetNodeId,
                    targetHandle: 'top',
                    type: 'custom',
                    animated: true,
                    style: downStyle,
                    data: {
                        connection_type: conn.connectionType,
                        is_uplink: false,
                        originalStyle: downStyle,
                        hideLabel: isAP,
                    },
                });
            }
        });

        setNodes(detailNodes);
        setEdges(detailEdges);
        setViewMode('detail');
        setFocusedSwitchName(switchName);
        setFocusedSwitchId(switchId);
        setHasUnsavedChanges(false);

        setTimeout(() => {
            reactFlowInstanceRef.current?.fitView({ padding: 0.8, duration: 500 });
        }, 50);
    };

    const fetchSwitchDetails = useCallback(
        async (switchId: number, switchName: string, position: { x: number; y: number }, switchType: string, switchIp: string, hasProblem: boolean) => {
            try {
                setLoading(true);
                const client_id = level.name === "global" ? null : level.id;
                let interfaceData: any[] = [];
                let switchDetails: any = { name: switchName, id: switchId };

                if (switchType === 'firewall') {
                    const queryObj: any = { asset_id: switchId };
                    if (client_id) queryObj.client_id = client_id;

                    const response = await baseapi.get(`/firewall_interfaces`, {
                        params: {
                            query: JSON.stringify(queryObj),
                            limit: 500
                        }
                    });

                    if (response.data?.status) {
                        const rawList = response.data.data || [];
                        interfaceData = rawList
                            .filter((item: any) => {
                                let d: any = {};
                                if (typeof item.data === 'string') {
                                    try { d = JSON.parse(item.data); } catch { }
                                } else if (typeof item.data === 'object' && item.data !== null) {
                                    d = item.data;
                                }
                                const t = (d.type || item.type || '').toLowerCase();
                                return t === 'physical' || t === 'hard-switch';
                            })
                            .map((item: any, idx: number) => {
                                let d: any = {};
                                if (typeof item.data === 'string') {
                                    try { d = JSON.parse(item.data); } catch { }
                                } else if (typeof item.data === 'object' && item.data !== null) {
                                    d = item.data;
                                }
                                return {
                                    id: item.id,
                                    port_index: d.snmp_index || idx + 1,
                                    port_name: d.name || item.name || `port${idx + 1}`,
                                    port_number: d.name || item.name || `port${idx + 1}`,
                                    port_status: d.link_status || item.status || 'down',
                                    mac_address: d.mac_address || '',
                                    connected_to: null,
                                    type: d.type || 'physical',
                                    ip_address: d.ip_address || '',
                                    alias: d.alias || ''
                                };
                            });
                    }
                } else {
                    const response = await baseapi.get(`/interfaces/switch/${switchId}`, { params: { client_id } });
                    if (response.data.status && response.data.data) {
                        interfaceData = response.data.data.interfaces || [];
                        switchDetails = response.data.data.switch || {
                            name: switchName,
                            id: switchId,
                        };
                    }
                }

                const effectivePos = (switchDetails?.detail_position && typeof switchDetails.detail_position.x === 'number' && typeof switchDetails.detail_position.y === 'number')
                    ? { x: Number(switchDetails.detail_position.x), y: Number(switchDetails.detail_position.y) }
                    : position;

                processDetailData(
                    switchDetails.id,
                    switchDetails.name,
                    effectivePos,
                    interfaceData,
                    switchType,
                    switchIp,
                    hasProblem
                );
            } catch (error) {
                console.error('Failed to fetch switch/firewall details', error);
                showError('Failed to load interfaces');
                setViewMode('overview');
                setFocusedSwitchName('');
                setFocusedSwitchId(null);
            } finally {
                setLoading(false);
            }
        },
        [assets, level]
    );

    useEffect(() => {
        if (level.name === 'global') {
            navigate('/dashboard');
        } else {
            fetchAssets();
            fetchTopologyOverview();
        }
    }, [fetchAssets, fetchTopologyOverview, level, navigate]);

    useEffect(() => {
        const ids = new Set<number>();
        // Add assets that are marked as connected in the database
        assets.forEach((asset: any) => {
            if (asset.is_connected) {
                ids.add(asset.id);
            }
        });

        // Add assets that were loaded in the global overview
        overviewAssetIds.forEach(id => ids.add(id));

        // Add assets currently on the canvas (handles unsaved dragged items or current detail view)
        nodes.forEach((node) => {
            if (node.data.assetId) ids.add(node.data.assetId);
        });
        setUsedAssetIds(ids);
    }, [nodes, assets, overviewAssetIds]);


    const onNodeMouseEnter = useCallback(
        (_: React.MouseEvent, node: Node) => {
            setEdges((eds) =>
                eds.map((e) => {
                    const isConnected = e.source === node.id || e.target === node.id;
                    if (isConnected) {
                        return {
                            ...e,
                            style: {
                                ...e.style,
                                stroke: '#ff6a00ff',
                                strokeWidth: 5,
                                opacity: 2,
                                filter: 'drop-shadow(0px 0px 4px rgba(255, 106, 0, 0.8))',
                            },
                            zIndex: 10,
                        };
                    }
                    return { ...e, style: { ...e.style, opacity: 0.15 }, zIndex: 0 };
                })
            );
        },
        [setEdges]
    );

    const onNodeMouseLeave = useCallback(
        (_: React.MouseEvent, _node: Node) => {
            setEdges((eds) =>
                eds.map((e) => ({
                    ...e,
                    style: e.data?.originalStyle || {
                        stroke: '#3b82f6',
                        strokeWidth: 3,
                        opacity: 0.8,
                    },
                    zIndex: 0,
                }))
            );
        },
        [setEdges]
    );

    const onNodeClick = useCallback(
        (_event: React.MouseEvent, node: Node) => {
            const isSwitchType =
                node.data.assetType === 'switch' ||
                node.data.assetType === 'firewall' ||
                node.data.assetType === 'hub' ||
                node.data.assetType === 'unmanaged_switch' ||
                node.data.assetType === 'access_point';

            if (viewMode === 'overview' && (node.type === 'switchNode' || isSwitchType)) {
                const { assetId, switchName, assetName, assetType, assetIp, hasProblem } = node.data;
                fetchSwitchDetails(assetId, switchName || assetName, { x: 0, y: 0 }, assetType, assetIp, hasProblem);
            }

            if (node.data.assetType !== 'hub') {
                setIsPaletteOpen(false);
                setSidebarState({
                    open: true,
                    assetId: node.data.assetId,
                    assetName: node.data.assetName || node.data.switchName || 'Asset',
                    assetType: node.data.assetType || 'other',
                    assetIp: node.data.assetIp || '-',
                });
            }
        },
        [viewMode, fetchSwitchDetails]
    );

    const onEdgeClick = useCallback(
        (_event: React.MouseEvent, edge: Edge) => {
            if (viewMode === 'overview' && edge.data?.linkId) {
                setDeleteLinkModalState({
                    open: true,
                    edgeId: edge.id,
                    linkId: edge.data.linkId,
                });
            }
        },
        [viewMode]
    );

    const handleBackToOverview = () => {
        setViewMode('overview');
        setFocusedSwitchName('');
        setFocusedSwitchId(null);
        setSidebarState(prev => ({ ...prev, open: false }));
        fetchTopologyOverview();

        setTimeout(() => {
            reactFlowInstanceRef.current?.fitView({ padding: 0.8, duration: 500 });
        }, 100);
    };

    const handleSavePositions = async () => {
        if (!nodes.length) return;
        setSaving(true);
        const payload = nodes.map((node) => ({
            asset_id: node.data.assetId,
            x: Math.round(node.position.x),
            y: Math.round(node.position.y),
            fixed: true,
        }));

        try {
            const client_id = level.name === "global" ? null : level.id;
            const params: any = {};
            if (client_id) params.client_id = client_id;

            let response;
            if (viewMode === 'detail' && focusedSwitchId) {
                response = await baseapi.post(`/topology/details/${focusedSwitchId}/positions`, payload, { params });
            } else {
                response = await baseapi.post('/topology/positions', payload, { params });
            }

            if (response.data.status) {
                showSuccess('Layout saved successfully');
                setHasUnsavedChanges(false);
            } else {
                showError(response.data.message || 'Failed to save layout');
            }
        } catch (error: any) {
            showError(error.message || 'Failed to save layout');
        } finally {
            setSaving(false);
        }
    };

    const handleForceRefresh = async () => {
        try {
            setLoading(true);
            const client_id = level.name === "global" ? null : level.id;
            const response = await baseapi.post('/agents/agent-task', {
                job_type: "topology_force_refresh",
                client_id: client_id,
                parameters: {}
            });
            if (response.data.status) {
                showSuccess('Topology force refresh initiated');
            } else {
                showError(response.data.message || 'Failed to initiate force refresh');
            }
        } catch (error: any) {
            showError(error.message || 'Failed to trigger force refresh');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        await fetchTopologyOverview();
        showSuccess('Topology refreshed');
    };

    const onNodeDragStop = useCallback(() => {
        setHasUnsavedChanges(true);
    }, []);

    const handleAssetNodeDelete = useCallback(
        (nodeId: string) => {
            const instance = reactFlowInstanceRef.current;
            if (!instance) {
                setNodes((nds) => nds.filter((n) => n.id !== nodeId));
                setEdges((eds) =>
                    eds.filter((e) => e.target !== nodeId && e.source !== nodeId)
                );
                setHasUnsavedChanges(true);
                return;
            }

            const currentEdges = instance.getEdges();
            const currentNodes = instance.getNodes();

            // Find any edge connected to this node
            const connectedEdges = currentEdges.filter(
                (e: Edge) => e.target === nodeId || e.source === nodeId
            );

            for (const edge of connectedEdges) {
                // Determine the other node (presumably the parent/switch)
                const otherNodeId = edge.source === nodeId ? edge.target : edge.source;
                const otherNode = currentNodes.find((n: Node) => n.id === otherNodeId);

                // Check if the other node is a switch-like node with port mappings
                const isOtherNodeSwitch =
                    otherNode?.type === 'switchNode' ||
                    otherNode?.data?.assetType === 'switch' ||
                    otherNode?.data?.assetType === 'firewall' ||
                    otherNode?.data?.assetType === 'access_point' ||
                    otherNode?.data?.assetType === 'hub';

                if (isOtherNodeSwitch && otherNode?.data?.portMappings) {
                    // Identify which handle on the switch node is used
                    const switchHandle = edge.source === otherNodeId ? edge.sourceHandle : edge.targetHandle;

                    if (switchHandle) {
                        // Extract port ID from handles like 'port-123' or 'port-in-123'
                        const portIdMatch = switchHandle.match(/(\d+)$/);
                        if (portIdMatch) {
                            const portId = parseInt(portIdMatch[1], 10);
                            const port = otherNode.data.portMappings.find(
                                (p: any) => p.id === portId
                            );

                            if (port && port.id) {
                                const assetNode = currentNodes.find((n: Node) => n.id === nodeId);
                                setUnlinkModalState({
                                    open: true,
                                    interfaceId: port.id,
                                    nodeId: nodeId,
                                    assetName: assetNode?.data?.assetName || 'Asset',
                                });
                                return;
                            }
                        }
                    }
                }
            }

            // Default: remove from board directly
            setNodes((nds) => nds.filter((n) => n.id !== nodeId));
            setEdges((eds) =>
                eds.filter((e) => e.target !== nodeId && e.source !== nodeId)
            );
            setHasUnsavedChanges(true);
        },
        [setNodes, setEdges]
    );

    const isValidConnection = useCallback(
        (connection: Connection) => {
            if (connection.source === connection.target) return false;
            const { source, sourceHandle, target, targetHandle } = connection;

            const isPortOccupied = (nodeId: string | null, handleId: string | null) => {
                if (!nodeId || !handleId) return false;
                const normalized = handleId.replace('port-in-', '').replace('port-', '');
                if (!normalized || isNaN(parseInt(normalized, 10))) return false;

                return edges.some(e => {
                    const eSourceNorm = e.sourceHandle?.replace('port-in-', '').replace('port-', '');
                    const eTargetNorm = e.targetHandle?.replace('port-in-', '').replace('port-', '');

                    return (e.source === nodeId && eSourceNorm === normalized) ||
                        (e.target === nodeId && eTargetNorm === normalized);
                });
            };

            // If target is a switch port, check if it's taken
            if (isPortOccupied(target, targetHandle)) return false;

            // If source is a switch port, check if it's taken
            if (isPortOccupied(source, sourceHandle)) return false;

            return true;
        },
        [edges]
    );

    const onConnect = useCallback(
        (params: Connection) => {
            if (!params.source || !params.target) return;

            const sourceNode = nodes.find((n) => n.id === params.source);
            const targetNode = nodes.find((n) => n.id === params.target);

            if (!sourceNode || !targetNode) return;

            if (sourceNode.type === 'switchNode' && targetNode.type === 'assetNode') {
                setConnectionInfo({
                    sourceId: sourceNode.data.assetId,
                    targetId: targetNode.data.assetId,
                    sourceName: sourceNode.data.switchName,
                    targetName: targetNode.data.assetName,
                });
                setPendingConnection(params);
                setConnectionModalOpen(true);
                return;
            }

            if (sourceNode.type === 'assetNode' && targetNode.type === 'switchNode') {
                const portId = params.targetHandle
                    ? parseInt(params.targetHandle.replace('port-in-', '').replace('port-', ''), 10)
                    : null;
                if (!portId) {
                    showError('Invalid connection to switch/firewall.');
                    return;
                }

                setConnectionInfo({
                    sourceId: targetNode.data.assetId,
                    targetId: sourceNode.data.assetId,
                    sourceName: targetNode.data.switchName,
                    targetName: sourceNode.data.assetName,
                });

                setPendingConnection(params);
                setConnectionModalOpen(true);
                return;
            }

            if (sourceNode.type === 'assetNode' && targetNode.type === 'assetNode') {
                setConnectionInfo({
                    sourceId: sourceNode.data.assetId,
                    targetId: targetNode.data.assetId,
                    sourceName: sourceNode.data.assetName,
                    targetName: targetNode.data.assetName,
                });
                setPendingConnection(params);
                setTopologyLinkModalOpen(true);
                return;
            }

            showError('Invalid connection type.');
        },
        [nodes]
    );

    const confirmDeleteLink = async () => {
        const { linkId, edgeId } = deleteLinkModalState;
        if (!linkId || !edgeId) return;

        try {
            setIsDeletingLink(true);
            const client_id = level.name === "global" ? null : level.id;
            const response = await baseapi.delete(`/topology/link/${linkId}`, { params: { client_id } });

            if (response.data.status) {
                showSuccess('Connection removed successfully');
                setEdges((eds) => eds.filter((e) => e.id !== edgeId));
                setHasUnsavedChanges(true);
            } else {
                showError(response.data.message || 'Failed to remove connection');
            }
        } catch (error: any) {
            showError(error.response?.data?.message || 'Failed to remove connection');
        } finally {
            setIsDeletingLink(false);
            setDeleteLinkModalState({ open: false, edgeId: null, linkId: null });
        }
    };

    const handleConnectionConfirm = async (formData: any) => {
        if (!pendingConnection) return;

        let switchNode = nodes.find((n) => n.id === pendingConnection.source);
        let portId: number | null = null;
        let isUplink = false;

        if (switchNode?.type === 'switchNode') {
            const handle = pendingConnection.sourceHandle || '';
            portId = parseInt(handle.replace('port-', ''), 10);
            isUplink = false;
        } else {
            switchNode = nodes.find((n) => n.id === pendingConnection.target);
            const handle = pendingConnection.targetHandle || '';
            portId = parseInt(handle.replace('port-in-', ''), 10);
            isUplink = true;
        }

        if (!switchNode) {
            showError('Switch/Firewall node not found.');
            setConnectionModalOpen(false);
            return;
        }

        const interfaceObj = switchNode.data?.portMappings?.find(
            (p: any) => p.id === portId
        );

        if (!interfaceObj?.id) {
            showError('Port not configured. Try removing and adding the node again.');
            setConnectionModalOpen(false);
            return;
        }

        try {
            const client_id = level.name === "global" ? null : level.id;
            const payload = { ...formData, is_uplink: isUplink, client_id };
            const response = await baseapi.post(
                `/interfaces/${interfaceObj.id}/link-asset`,
                payload,
            );
            if (response.data.status) {
                showSuccess('Asset linked successfully');
                const linkStyle = { stroke: '#3b82f6', strokeWidth: 3, opacity: 0.6 };
                setEdges((eds) =>
                    addEdge(
                        {
                            ...pendingConnection,
                            id: `detail-edge-${pendingConnection.source}-${pendingConnection.target}`,
                            type: 'custom',
                            animated: true,
                            style: linkStyle,
                            data: { ...payload, originalStyle: linkStyle },
                        },
                        eds
                    )
                );
            } else {
                showError(response.data.message || 'Failed to link asset');
            }
        } catch (err: any) {
            showError(err.response?.data?.message || 'Failed to link asset');
        } finally {
            setConnectionModalOpen(false);
            setPendingConnection(null);
            setConnectionInfo(null);
        }
    };

    const handleTopologyLinkConfirm = async (data: any) => {
        if (!pendingConnection || !connectionInfo) return;
        const payload = {
            source_asset_id: connectionInfo.sourceId,
            destination_asset_id: connectionInfo.targetId,
            connection_type: data.connection_type,
            is_uplink: data.is_uplink,
        };
        try {
            const client_id = level.name === "global" ? null : level.id;
            const response = await baseapi.post('/topology/link', payload, { params: { client_id } });
            if (response.data.status) {
                showSuccess('Assets linked successfully');
                const newLinkId = response.data.data?.id || response.data.id;
                const topLinkStyle = { stroke: '#10b981', strokeWidth: 3, opacity: 0.8 };
                setEdges((eds) =>
                    addEdge(
                        {
                            ...pendingConnection,
                            id: `edge-${pendingConnection.source}-${pendingConnection.target}`,
                            type: 'custom',
                            animated: true,
                            style: topLinkStyle,
                            data: { ...data, linkId: newLinkId, originalStyle: topLinkStyle },
                        },
                        eds
                    )
                );
            } else showError(response.data.message);
        } catch (err: any) {
            showError(err.response?.data?.message || 'Failed to link assets');
        } finally {
            setTopologyLinkModalOpen(false);
            setPendingConnection(null);
            setConnectionInfo(null);
        }
    };

    const confirmUnlink = async () => {
        const { interfaceId, nodeId } = unlinkModalState;
        if (!interfaceId || !nodeId) return;
        try {
            setIsUnlinking(true);
            const client_id = level.name === "global" ? null : level.id;
            const response = await baseapi.delete(`/interfaces/${interfaceId}/unlink`, { params: { client_id } });
            if (response.data.status) {
                showSuccess('Asset unlinked successfully');
                fetchAssets();

                setNodes((nds) => nds.filter((n) => n.id !== nodeId));
                setEdges((eds) =>
                    eds.filter((e) => e.target !== nodeId && e.source !== nodeId)
                );
                setHasUnsavedChanges(true);

            } else {
                showError(response.data.message);
            }

        } catch (error: any) {
            showError(error.response?.data?.message || 'Failed to unlink asset');
        } finally {
            setIsUnlinking(false);
            setUnlinkModalState({
                open: false,
                interfaceId: null,
                nodeId: null,
                assetName: '',
            });
        }
    };

    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const generateDefaultPorts = (count: number) => {
        return Array.from({ length: count }, (_, i) => ({
            id: 100000 + i,
            port_index: i + 1,
            port_name: `Port ${i + 1}`,
            port_status: 'down',
            connected_to: null,
        }));
    };

    const onDrop = useCallback(
        async (event: React.DragEvent) => {
            event.preventDefault();
            if (!reactFlowInstanceRef.current) return;

            const originalType = event.dataTransfer.getData('application/reactflow');
            const assetType = event.dataTransfer.getData('assetType');
            const assetIdStr = event.dataTransfer.getData('assetId');

            if (!originalType) return;

            const realAssetId = parseInt(assetIdStr.replace('asset-', ''), 10);
            if (usedAssetIds.has(realAssetId)) {
                showError('Asset is already on the board');
                return;
            }

            const position = reactFlowInstanceRef.current.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            const isSwitchLike =
                assetType === 'switch' ||
                assetType === 'firewall' ||
                assetType === 'hub' ||
                originalType === 'switchNode';
            const nodeType = isSwitchLike ? 'switchNode' : 'assetNode';

            let portMappings = undefined;
            if (isSwitchLike) {
                const totalPorts =
                    parseInt(event.dataTransfer.getData('totalPorts'), 10) || 24;
                portMappings = generateDefaultPorts(totalPorts);
            }

            const newNode: Node = {
                id: assetIdStr || getNodeId(),
                type: nodeType,
                position,
                data: {
                    switchName: event.dataTransfer.getData('assetName') || 'New',
                    assetName: event.dataTransfer.getData('assetName') || 'New',
                    assetType: assetType || 'server',
                    assetIp: event.dataTransfer.getData('assetIp') || '0.0.0.0',
                    assetId: realAssetId,
                    portMappings,
                    onDelete: handleAssetNodeDelete,
                    onPortClick: (port: any) => handlePortClick(port),
                },
            };

            setNodes((nds) => nds.concat(newNode));
            setHasUnsavedChanges(true);

            if (isSwitchLike) {
                try {
                    const client_id = level.name === "global" ? null : level.id;
                    const response = await baseapi.get(
                        `/interfaces/switch/${realAssetId}`,
                        { params: { client_id } }
                    );
                    if (response.data.status && response.data.data) {
                        const realInterfaces = response.data.data.interfaces || [];
                        const formattedInterfaces = realInterfaces.map((inf: any) => ({
                            ...inf,
                            connected_to: inf.connected_asset || null,
                        }));
                        setNodes((nds) =>
                            nds.map((node) => {
                                if (node.data.assetId === realAssetId) {
                                    return {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            portMappings: formattedInterfaces,
                                            onPortClick: (port: any) => handlePortClick(port),
                                        },
                                    };
                                }
                                return node;
                            })
                        );
                        showSuccess(`Loaded ${assetType} configuration`);
                    }
                } catch (error) {
                    console.error('Failed to load interface configurations', error);
                    showError(
                        `Warning: Failed to load ${assetType} ports configuration`
                    );
                }
            }
        },
        [setNodes, usedAssetIds, handleAssetNodeDelete, level, handlePortClick]
    );

    const onInit = useCallback((instance: ReactFlowInstance) => {
        reactFlowInstanceRef.current = instance;
    }, []);

    return (
        <div className="w-full h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white font-sans flex pb-20 overflow-hidden">
            {isPaletteOpen && (
                <AssetsPalette
                    assets={assets}
                    loading={loading}
                    onAssetAdded={() => { fetchAssets(); fetchTopologyOverview(); }}
                    usedAssetIds={usedAssetIds}
                    callTopology={fetchTopologyOverview}
                />
            )}
            <div className="flex-1 relative h-full">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    connectionRadius={30}
                    connectionMode={ConnectionMode.Loose}
                    onNodeDragStop={onNodeDragStop}
                    onNodeClick={onNodeClick}
                    onEdgeClick={onEdgeClick}
                    onNodeMouseEnter={onNodeMouseEnter}
                    onNodeMouseLeave={onNodeMouseLeave}
                    onInit={onInit}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    isValidConnection={isValidConnection}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    minZoom={0.3}
                    fitView
                    fitViewOptions={{ padding: 0.8 }}
                    style={{ background: isDark ? '#111827' : '#f8fafc' }}
                    proOptions={{ hideAttribution: true }}
                    deleteKeyCode={null}
                >
                    <Background
                        variant={BackgroundVariant.Dots}
                        gap={24}
                        size={1}
                        color={isDark ? '#374151' : '#cbd5e1'}
                    />
                    <Controls className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !fill-gray-600 dark:!fill-gray-400 [&>button]:!border-gray-200 dark:[&>button]:!border-gray-700 [&>button:hover]:!bg-gray-100 dark:[&>button:hover]:!bg-gray-700 shadow-md" />
                    <Panel position="top-right" className="m-4 flex flex-col gap-2 items-end">
                        <div className="flex gap-2">
                            {!isPaletteOpen && !sidebarState.open && (
                                <button
                                    onClick={() => setIsPaletteOpen(true)}
                                    className="flex text-xs items-center gap-2 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-800 dark:text-white px-3 py-2 rounded-lg shadow-md font-medium transition-colors border border-gray-200 dark:border-gray-700 cursor-pointer"
                                >
                                    <LayoutGrid className="w-4 h-4 text-orange-500" /> Show Assets
                                </button>
                            )}

                            <div
                                className="transition-all duration-300 transform"
                            >
                                <button
                                    onClick={handleSavePositions}
                                    disabled={saving}
                                    className="flex items-center text-xs gap-2 bg-blue-600 hover:bg-blue-700 text-white px-2 py-2 rounded-lg shadow-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" /> Save Layout
                                        </>
                                    )}
                                </button>
                            </div>

                            {viewMode === 'detail' && (
                                <button
                                    onClick={handleBackToOverview}
                                    className="flex text-xs items-center gap-2 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-2 py-2 rounded-lg shadow-md font-medium transition-colors border border-gray-200 dark:border-gray-600 cursor-pointer"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Back to Overview
                                </button>
                            )}

                            {viewMode === 'overview' && switchGroups.length > 0 && (
                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        className="flex items-center justify-between gap-2 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-800 dark:text-white text-xs px-3 py-2 rounded-lg shadow-md font-medium border border-gray-200 dark:border-gray-700 transition-all cursor-pointer min-w-[140px]"
                                    >
                                        <span className="truncate">
                                            {selectedGroupId === 'all'
                                                ? 'All Groups'
                                                : switchGroups.find(g => g.id.toString() === selectedGroupId)?.name || 'All Groups'}
                                        </span>
                                        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {isDropdownOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -8 }}
                                                transition={{ duration: 0.15 }}
                                                className="absolute right-0 mt-1.5 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-2xl overflow-hidden z-[1000] backdrop-blur-md"
                                            >
                                                <div className="max-h-60 overflow-y-auto py-1">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedGroupId('all');
                                                            fetchTopologyOverview('all');
                                                            setIsDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-xs transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${selectedGroupId === 'all' ? 'text-blue-600 dark:text-blue-400 font-bold bg-blue-50 dark:bg-blue-500/5' : 'text-gray-700 dark:text-gray-300'}`}
                                                    >
                                                        All Groups
                                                    </button>
                                                    {switchGroups.map((g: any) => (
                                                        <button
                                                            key={g.id}
                                                            onClick={() => {
                                                                const val = g.id.toString();
                                                                setSelectedGroupId(val);
                                                                fetchTopologyOverview(val);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className={`w-full text-left px-3 py-2 text-xs transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 truncate cursor-pointer ${selectedGroupId === g.id.toString() ? 'text-blue-600 dark:text-blue-400 font-bold bg-blue-50 dark:bg-blue-500/5' : 'text-gray-700 dark:text-gray-300'}`}
                                                        >
                                                            {g.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>
                    </Panel>
                    <Panel position="top-center" className="mt-4">
                        <div className="flex items-center gap-2 px-4 py-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur border border-gray-200 dark:border-gray-700 rounded-full shadow-lg">
                            <LayoutGrid className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                            <span className="text-xs font-bold text-gray-700 dark:text-gray-300 tracking-wider uppercase">
                                {viewMode === 'overview'
                                    ? 'Network Overview'
                                    : `${focusedSwitchName}`}
                            </span>

                            {viewMode === 'overview' && (
                                <div className="flex items-center gap-1.5 ml-3 pl-3 border-l border-gray-200 dark:border-gray-700">
                                    <button
                                        onClick={handleForceRefresh}
                                        disabled={loading}
                                        className="p-1.5 flex items-center gap-2 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/20 rounded-md transition-all disabled:opacity-50 cursor-pointer"
                                        title="Force Refresh"
                                    >
                                        <Zap className={`w-3.5 h-3.5 ${loading ? 'animate-pulse' : ''}`} />
                                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300 tracking-wider uppercase">
                                            Force Refresh
                                        </span>
                                    </button>
                                    <button
                                        onClick={handleRefresh}
                                        disabled={loading}
                                        className="p-1.5 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 rounded-md transition-all disabled:opacity-50 cursor-pointer"
                                        title="Refresh"
                                    >
                                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </Panel>
                </ReactFlow>
            </div>

            <AddConnectionModal
                open={connectionModalOpen}
                onClose={() => setConnectionModalOpen(false)}
                onConfirm={handleConnectionConfirm}
                assetId={connectionInfo?.targetId || null}
                assetName={connectionInfo?.targetName}
            />
            <TopologyLinkModal
                open={topologyLinkModalOpen}
                onClose={() => setTopologyLinkModalOpen(false)}
                onConfirm={handleTopologyLinkConfirm}
                sourceName={connectionInfo?.sourceName}
                targetName={connectionInfo?.targetName}
            />
            <UnlinkModal
                open={unlinkModalState.open}
                onClose={() =>
                    setUnlinkModalState({
                        open: false,
                        interfaceId: null,
                        nodeId: null,
                        assetName: '',
                    })
                }
                onConfirm={confirmUnlink}
                assetName={unlinkModalState.assetName}
                isSubmitting={isUnlinking}
            />
            <DeleteLinkModal
                open={deleteLinkModalState.open}
                onClose={() =>
                    setDeleteLinkModalState({ open: false, edgeId: null, linkId: null })
                }
                onConfirm={confirmDeleteLink}
                isSubmitting={isDeletingLink}
            />
            <AssetDetailSidebar
                open={sidebarState.open}
                assetId={sidebarState.assetId}
                assetName={sidebarState.assetName}
                assetType={sidebarState.assetType}
                assetIp={sidebarState.assetIp}
                onClose={() => {
                    setSidebarState(prev => ({ ...prev, open: false }));
                    setIsPaletteOpen(true);
                }}
            />
        </div>
    );
}
